import { IMultiYearComparisonResponseDTO, IMultiYearNodeDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { ZERO_TOLERANCE } from './multi-year-format.util';

/**
 * Walk the harmonized tree and collect every expandable node id (any node with
 * children that isn't a SUB_TOTAL or ACCOUNT — those are leaves visually).
 * Section roots (assets / liabilities / income-statement) are always rendered
 * expanded and have no toggle, so they are skipped (level 0).
 *
 * Used by both the reducer (`expandAllNodes` action) and the container
 * (`computeIsAllExpanded` for the show/hide-all toggle).
 */
export function collectExpandableNodeIds(harmonized: IMultiYearComparisonResponseDTO): string[] {
    const ids: string[] = [];
    const walk = (node: IMultiYearNodeDTO | null, level: number): void => {
        if (node == null) return;
        if (level > 0 && node.children.length > 0 && node.type !== 'ACCOUNT' && node.type !== 'SUB_TOTAL') {
            ids.push(node.id);
        }
        for (const child of node.children) {
            walk(child, level + 1);
        }
    };
    walk(harmonized.assets, 0);
    walk(harmonized.liabilities, 0);
    walk(harmonized.incomeStatement, 0);
    return ids;
}

/**
 * Walk the harmonized tree and collect IDs of GROUP nodes only (not FLI /
 * SUB_FLI / OF_THAT_ITEM). Section roots at level 0 are always-expanded and
 * have no toggle, so they are excluded.
 *
 * Used by `collapseAllNodes` to keep groups at their current state while
 * collapsing only FLI-level nodes.
 */
export function collectGroupNodeIds(harmonized: IMultiYearComparisonResponseDTO): string[] {
    const ids: string[] = [];
    const walk = (node: IMultiYearNodeDTO | null, level: number): void => {
        if (node == null) return;
        if (level > 0 && node.type === 'GROUP' && node.children.length > 0) {
            ids.push(node.id);
        }
        for (const child of node.children) {
            walk(child, level + 1);
        }
    };
    walk(harmonized.assets, 0);
    walk(harmonized.liabilities, 0);
    walk(harmonized.incomeStatement, 0);
    return ids;
}

/** True iff every selected-year value on `node` is within `ZERO_TOLERANCE` of zero. */
export function isAllZeroAcrossYears(node: IMultiYearNodeDTO, selectedYearIndices: number[]): boolean {
    return selectedYearIndices.every((i) => Math.abs(node.values[i] ?? 0) < ZERO_TOLERANCE);
}

/**
 * True iff `node` AND every descendant are zero across the selected years.
 * Used by both the on-screen view (`row-vm.builder.ts`) and the export modal
 * to hide branches that wouldn't contribute any visible numbers.
 */
export function hasNoNonZeroDescendantInYears(
    node: IMultiYearNodeDTO,
    selectedYearIndices: number[],
): boolean {
    if (!isAllZeroAcrossYears(node, selectedYearIndices)) return false;
    if (node.children.length === 0) return true;
    return node.children.every((c) => hasNoNonZeroDescendantInYears(c, selectedYearIndices));
}
